import {
  Button,
  Flex,
  Form,
  FormInstance,
  Input,
  Row,
  SubTitle,
  styled,
  useTheme,
} from '@teko/ds-desktop';
import { ThemeProps } from '@teko/ui';

import { HomeLocationColored, LocationOutlined } from '@teko/ds-icon';
import { Area } from '@teko/location';
import { Spinner } from 'components/desktop/atoms';
import { useDistanceTerminalContext, useTenantConfig } from 'contexts';
import { tSelectPlaceholder } from 'helpers/translate';
import { useSuggestionAddress } from 'hooks';
import { t } from 'i18n';
import { STEP_LOCATION } from '../../usePickLocation';
import { MAX_LENGTH_NOTE } from '../../utils';
import { AddressForm } from '../ModeEnterAddressV2';
import { SwitchGetByGeoLocation } from '../SwitchGetByGeoLocation';
import { EnterAddressItem } from './EnterAddressItem';

const COUNT_STRING_WITHOUT_STREET_ADDRESS = 4;
export const EnterAddress = ({
  province,
  ward,
  setStepLocation,
  form,
  setPlace,
  onFinish,
}: {
  province?: Area;
  ward?: Area;
  setStepLocation: (stepAddress: STEP_LOCATION) => void;
  form: FormInstance<AddressForm>;
  setPlace: (place: { lat: number; long: number }) => void;
  onFinish: (address: AddressForm) => void;
}) => {
  const theme = useTheme();
  const { epicOmni1825SuggestAddress } = useTenantConfig();

  const {
    isGetCurrentLocation,
    isLoadingGetDistanceTerminals,
    messageError,
    setMessageError,
  } = useDistanceTerminalContext();

  const {
    dataSuggestion,
    setSuggestInputText,
    openListSuggestion,
    setOpenListSuggestion,
    isLoadingSuggest,
    getPlaceDetails,
  } = useSuggestionAddress({
    province: province?.title || '',
    ward: ward?.title || '',
  });

  const onClickSuggest = async (placeId: string) => {
    try {
      const items = await getPlaceDetails(placeId);
      if (!items || !Array.isArray(items) || !items.length) {
        return;
      }
      // items là danh sách các placeDetail và cần lấy placeDetail đầu tiên
      // trong case này chỉ gửi 1 placeID lên nên chỉ có 1 placeDetail
      const item = items?.[0];
      const address =
        item.fullAddress
          .split(',')
          ?.splice(
            0,
            item.fullAddress.split(',').length -
              COUNT_STRING_WITHOUT_STREET_ADDRESS,
          )
          ?.join(',') ||
        item.fullAddress ||
        '';
      setOpenListSuggestion(false);
      setPlace({ lat: item.lat, long: item.long });
      const newValue = {
        provinceCode: item.provinceCode || undefined,
        ...(item.wardCode ? { wardCode: item.wardCode } : {}),
      };
      form.setFieldsValue({
        address,
        ...newValue,
      });
    } catch (error) {
      setMessageError?.(t('Đã có lỗi xảy ra, vui lòng thử lại'));
    }
  };

  return (
    <>
      <Row justify="start">
        <HomeLocationColored size={20} />
        <SubTitle
          style={{
            marginLeft: 4,
            fontSize: 14,
            color: theme.colors.black,
          }}
        >
          {t('Địa chỉ nhận hàng')}:
        </SubTitle>
      </Row>
      <SwitchGetByGeoLocation />

      <StyledForm
        onFinish={(address) => onFinish(address as AddressForm)}
        layout="vertical"
        form={form}
      >
        <Form.Item
          label={t('Tỉnh/Thành phố')}
          trigger="input"
          name="provinceCode"
          rules={[
            {
              required: !isGetCurrentLocation,
              message: t('${label} không được bỏ trống'),
            },
          ]}
          validateTrigger="input"
        >
          <EnterAddressItem
            placeHolder={tSelectPlaceholder('Tỉnh/Thành phố')}
            valueInput={province?.title || ''}
            onClick={() => {
              setStepLocation('PROVINCE');
            }}
            disabled={!!isGetCurrentLocation}
          />
        </Form.Item>
        <Form.Item
          label={t('Phường/Xã')}
          trigger="input"
          name="wardCode"
          rules={[
            {
              required: !isGetCurrentLocation,
              message: t('${label} không được bỏ trống'),
            },
          ]}
          validateTrigger="input"
        >
          <EnterAddressItem
            placeHolder={tSelectPlaceholder('Phường/Xã')}
            valueInput={ward?.title || ''}
            onClick={() => {
              setStepLocation('WARD');
            }}
            disabled={!!isGetCurrentLocation}
          />
        </Form.Item>
        <Form.Item
          label={t('Địa chỉ cụ thể')}
          name="address"
          rules={[
            {
              required: !isGetCurrentLocation,
              message: t('${label} không được bỏ trống'),
            },
          ]}
        >
          <Input
            disabled={isGetCurrentLocation}
            placeholder={t('Số nhà, ngõ, tên đường...')}
            onChange={(text) => {
              setSuggestInputText(text);
              setOpenListSuggestion(true);
            }}
          />
        </Form.Item>
        {epicOmni1825SuggestAddress &&
          !!dataSuggestion &&
          !!openListSuggestion &&
          (!!dataSuggestion.length || isLoadingSuggest) && (
            <SuggestWrapper>
              {isLoadingSuggest ? (
                <Flex justify="center" align="center">
                  <Spinner />
                </Flex>
              ) : (
                <>
                  <Text style={{ textAlign: 'left' }}>{t('Gợi ý')}</Text>
                  {dataSuggestion.map((item) => {
                    return (
                      <ItemSuggestion
                        key={item.fullAddress}
                        onClick={() => onClickSuggest(item.placeId)}
                      >
                        <LocationOutlined />
                        <Text style={{ textAlign: 'left', marginLeft: 8 }}>
                          {item.fullAddress}
                        </Text>
                      </ItemSuggestion>
                    );
                  })}
                </>
              )}
            </SuggestWrapper>
          )}
        <Form.Item label={t('Ghi chú')} name="addressNote" required={false}>
          <Input
            disabled={isGetCurrentLocation}
            maxLength={MAX_LENGTH_NOTE}
            placeholder={t('Ví dụ: Cổng màu xanh, nhà bên cạnh cây xăng')}
          />
        </Form.Item>
        {messageError && <WrapperError>{messageError}</WrapperError>}

        <StyledButton
          disabled={isLoadingGetDistanceTerminals}
          block
          type="submit"
          variant="primary"
        >
          {isLoadingGetDistanceTerminals && <Spinner />}
          {t('Xác nhận')}
        </StyledButton>
      </StyledForm>
    </>
  );
};

const StyledForm = styled(Form)`
  .teko-form-item {
    margin-bottom: 0px;
  }
`;

const StyledButton = styled(Button)`
  margin-top: 30px;
`;

export const SuggestWrapper = styled.div`
  background-color: #fafafa;
  border-radius: 12px;
  padding: 8px;
  margin-bottom: 15px;
  max-height: 200px;
  overflow-y: scroll;
`;

const Text = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #434657;
`;

export const ItemSuggestion = styled.div`
  padding: 8px 10px;
  cursor: pointer;
`;

export const WrapperError = styled.div<ThemeProps>(
  ({ theme }) => `
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${theme.colors.warning};
  background-color: #FFFBE6;
  padding: 8px;
`,
);
